import {
    PropsWithChildren,
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { ISuspensionNotice } from "../interfaces/suspension-notice.interface";
import { getOrganizationStatus } from "../services/organization-status/organization-status.service";

interface ContextState {
    notice: ISuspensionNotice;
    isResolved: boolean;
}

const EMPTY_NOTICE: ISuspensionNotice = {
    isSuspended: false,
    title: null,
    message: null,
};

const defaultContext: ContextState = {
    notice: EMPTY_NOTICE,
    isResolved: false,
};

const SuspensionContext = createContext<ContextState>(defaultContext);

const SuspensionProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [notice, setNotice] = useState<ISuspensionNotice>(EMPTY_NOTICE);
    const [isResolved, setIsResolved] = useState(false);

    useEffect(() => {
        let cancelled = false;

        // Raised by the httpClient error interceptor when any request 403s with
        // the suspension code, so an org suspended mid-session flips over on the
        // next API response without a reload.
        const onSuspended = (event: Event) => {
            const detail = (event as CustomEvent<ISuspensionNotice>).detail;

            setNotice({
                isSuspended: true,
                title: detail?.title ?? null,
                message: detail?.message ?? null,
            });
        };

        window.addEventListener("organization:suspended", onSuspended);

        getOrganizationStatus()
            .then((result) => {
                if (!cancelled && result.isSuspended) setNotice(result);
            })
            // A failed status call must never block render — resolve to
            // not-suspended and let the SPA boot normally.
            .catch(() => undefined)
            .finally(() => {
                if (!cancelled) setIsResolved(true);
            });

        return () => {
            cancelled = true;
            window.removeEventListener("organization:suspended", onSuspended);
        };
    }, []);

    const value = useMemo(() => ({ notice, isResolved }), [notice, isResolved]);

    return (
        <SuspensionContext.Provider value={value}>
            {children}
        </SuspensionContext.Provider>
    );
};

const useSuspensionContext = () => useContext(SuspensionContext);

export { SuspensionProvider, useSuspensionContext };
